import { LinearProgress, linearProgressClasses } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { type SystemStyleObject } from '@mui/system';

export const BannerLinearProgress = styled(LinearProgress)(() => ({
	height: 8,
	borderRadius: 4,
	[`&.${linearProgressClasses.colorPrimary}`]: {
		backgroundColor: 'rgba(0, 0, 0, 0.35)',
	},
	[`& .${linearProgressClasses.bar}`]: {
		borderRadius: 4,
		backgroundColor: '#FFFFFF',
	},
}));

export const bannerButtonHoverStyles = {
	borderColor: 'white',
	backgroundColor: 'rgba(255, 255, 255, 0.1)',
};

export const bannerButtonStyles = {
	color: 'white',
	borderColor: 'white',
	borderRadius: '2rem',
	'&:hover': bannerButtonHoverStyles,
};

export const getHeroContainerStyles = (providerHeroTheme: string, theme: Theme, hasProviderPhotos: boolean) => ({
	display: 'flex',
	flexDirection: { xs: 'column-reverse', md: 'row' },
	backgroundImage:
		providerHeroTheme === 'light'
			? theme.palette.backgrounds?.lightGradient || ''
			: theme.palette.backgrounds?.darkGradient,
	height: 'auto',
});

export const getHeroImageWrapperStyles = {
	width: { xs: '100%', md: '50%' },
	height: { xs: '100%', md: 'auto' },
	//added because PDP hero needs image to be static or it looks weird
	'[data-testid="section_aspect_ratio_padding_box"]': {
		position: { md: 'static' },
	},
	'.MuiBox-root > img.MuiBox-root': {
		objectFit: 'cover !important',
		objectPosition: { xs: 'center center !important', md: 'top left !important' },
	},
};

export const getHeroContentContainerStyles = (theme: Theme, hasProviderPhotos: boolean): SystemStyleObject<Theme> => ({
	display: 'flex',
	flexDirection: 'column',
	gap: '1rem',
	px: { xs: '1rem', md: '1.5rem', lg: '2rem' },
	py: { xs: '1rem', md: '3.75rem' },
	boxSizing: 'border-box',
	[theme.breakpoints.up('md')]: {
		width: hasProviderPhotos ? '50%' : '100%',
		...(!hasProviderPhotos ? { alignItems: 'center' } : {}),
	},
});

export const heroTitleStyles = {
	fontWeight: '400',
	letterSpacing: '-1px',
	textAlign: 'left',
};

export const heroQuoteStyles = {
	fontFamily: 'var(--Brand-Typography-L3-Heading-Typeface, "Beausite Classic")',
	fontSize: 'var(--Brand-Typography-L3-Heading-Size, 28px)',
	fontStyle: 'normal',
	fontWeight: 500,
	lineHeight: 'var(--Brand-Typography-L3-Heading-Line-Height, 36px)',
	letterSpacing: 'var(--Brand-Typography-L3-Heading-Letter-Spacing, -0.5px)',
};

export const getHeroLabelsListStyles = (hasProviderPhotos: boolean) => ({
	display: 'flex',
	flexDirection: hasProviderPhotos ? 'column' : 'row',
	alignItems: hasProviderPhotos ? 'flex-start' : 'center',
	justifyContent: hasProviderPhotos ? 'flex-start' : 'center',
	gap: '1rem',
});

export const getHeroLabelsItemStyles = (textColor: string) => ({
	display: 'flex',
	gap: '.5rem',
	flexGrow: { xs: '1', md: '0' },
	color: textColor,
	justifyContent: 'flex-start',
});

export const getHeroLabelsButtonStyles = (textColor: string) => ({
	display: 'flex',
	flexGrow: { xs: '1', md: '0' },
	color: textColor,
	justifyContent: 'flex-start',
	padding: '0',
});

export const getProviderContentContainerStyles = {
	display: 'flex',
	flexDirection: 'column',
	gap: '1rem',
	px: { xs: '1rem', md: '1.5rem', lg: '2rem' },
	py: { xs: '1rem', md: '1.5rem' },
	boxSizing: 'border-box',
};

export const providerAboutTitleStyles = {
	paddingBottom: '.5rem',
};

export const aboutListStyles = {
	display: 'flex',
	gap: '1rem',
	flexWrap: 'wrap',
};

export const aboutListItemStyles = {
	width: { xs: '100%', md: 'calc(50% - 0.5rem)' },
};

export const aboutSubTitleListStyles = {
	display: 'flex',
	flexWrap: 'wrap',
	paddingBottom: '2rem',
};

export const aboutSubTitleListMiddotStyles = {
	px: '.25rem',
};

export const providerAboutSubTitleListBoxStyle = {
	display: 'flex',
};

export const aboutListItemTitleStyles = {
	pb: '0.25rem',
};

export const tabLinkStyles = {
	'&.active': {
		'& .MuiTypography-root': {
			borderBottom: '2px solid',
			borderColor: 'text.interactive',
			color: 'text.interactive',
			fontWeight: 600,
		},
	},
	textDecoration: 'none',
};

export const tabLinkTextStyles = {
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	height: '3rem',
	lineHeight: '3rem',
	display: 'inline-block', // makes the link 100% height so that user doesn't have to click exactly on the text
	padding: '0 1rem',
	fontSize: '1rem',
	color: 'text.tertiary',
};

export const tabContainerStyles = {
	display: 'flex',
	overflow: 'auto',
	justifyContent: 'center',
};

export const specialtiesListStyles = {
	display: 'flex',
	flexWrap: 'wrap',
	gap: '0.5rem',
};

export const galleryWrapperStyles = {
	display: 'flex',
	gap: '1.25rem',
	flexWrap: 'wrap',
};

export const getGalleryImageWrapperStyles = {
	width: { xs: '100%', md: 'calc(50% - 0.625rem)' },
	height: 'auto',
	maxHeight: '38.125rem',
};

export const providerOfficesTitleStyles = {
	textAlign: 'center',
};

export const getMapAndLocationsComponentStyles = (isSmallScreen?: boolean, fullWidth?: boolean) => ({
	display: 'flex',
	height: 'auto',
	borderRadius: fullWidth ? '0' : '0.5rem',
	marginBottom: fullWidth ? '0' : '1.25rem',
	justifyContent: 'flex-start',
});

export const getLocationsCardContainerStyles = (theme: Theme, isSmallScreen?: boolean) => ({
	display: 'flex',
	flexDirection: isSmallScreen ? 'row' : 'column',
	flexWrap: 'nowrap',
	borderBottom: isSmallScreen ? 'none' : '0.0625rem solid',
	borderLeft: isSmallScreen ? 'none' : '0.0625rem solid',
	borderTop: isSmallScreen ? 'none' : '0.0625rem solid',
	borderRadius: isSmallScreen ? '0.5rem' : '0.5rem 0rem 0rem 0.5rem',
	borderColor: 'neutrals.primary',
	backgroundColor: isSmallScreen ? `${theme.palette.backgrounds?.white}` : `${theme.palette.backgrounds?.light}`,
	overflow: 'scroll',
	paddingTop: '1rem',
	paddingBottom: '1rem',
	px: isSmallScreen ? '1rem' : '',
	'::-webkit-scrollbar': {
		display: 'none',
	},
	msOverflowStyle: 'none',
	scrollBarWidth: 'none',
	height: { xs: 'auto', md: '100%' },
	position: 'relative',
});

export const providerQuotesStyles = {
	container: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		textAlign: 'center',
		gap: '1rem',
		py: { xs: '1.5rem', md: '2rem' },
		px: { xs: '1rem', md: '1.5rem', lg: '2rem' },
		borderRadius: '8px',
		maxWidth: '100%',
		boxSizing: 'border-box',
		overflow: 'hidden',
		position: 'relative',
		background:
			'var(--Background-Gradient-Dark, linear-gradient(165deg, var(--Brand-Colors-Background-Gradient---Dark-Dark, #0A2F54) 4.91%, var(--Brand-Colors-Background-Gradient---Dark-Light, #1A4C80) 89.46%))',
	},
	quoteText: {
		fontFamily: 'var(--Brand-Typography-L3-Heading-Typeface, "Beausite Classic")',
		fontSize: 'var(--Brand-Typography-L3-Heading-Size, 22px)',
		fontStyle: 'normal',
		fontWeight: 500,
		lineHeight: 'var(--Brand-Typography-L3-Heading-Line-Height, 28px)',
		letterSpacing: 'var(--Brand-Typography-L3-Heading-Letter-Spacing, -0.5px)',
		color: 'var(--Brand-Colors-Text-Light, #FFF)',
		maxWidth: '800px',
		position: 'relative',
		'&::before': {
			fontSize: { xs: '3rem', md: '4rem' },
			color: 'primary.main',
			opacity: 0.3,
			position: 'absolute',
			top: { xs: '-0.5rem', md: '-1rem' },
			left: { xs: '-0.5rem', md: '-1rem' },
			fontFamily: 'Georgia, serif',
		},
	},
	attribution: {
		mt: '0.5rem',
		fontStyle: 'normal',
		color: 'var(--Brand-Colors-Text-Light, #FFF)',
		textAlign: 'center',
		fontFamily: 'var(--Brand-Typography-Body-Large---Book-Typeface, "Beausite Classic")',
		fontSize: 'var(--Brand-Typography-Body-Large---Book-Size, 18px)',
		fontWeight: 350,
		lineHeight: 'var(--Brand-Typography-Body-Large---Book-Line-Height, 28px)',
		letterSpacing: 'var(--Brand-Typography-Body-Large---Book-Letter-Spacing, 0)',
	},
};

export const featuredProviderReviewsStyles = {
	container: {
		display: 'flex',
		flexDirection: 'column',
		gap: '1.5rem',
		py: { xs: '2.5rem', sm: '3.75rem' },
		scrollMarginTop: '2.5rem',
	},
	banner: {
		display: 'flex',
		flexDirection: { xs: 'column', md: 'row' },
		alignItems: { xs: 'flex-start', md: 'center' },
		gap: { xs: '1.5rem', md: '2.5rem' },
		p: { xs: '1.5rem', md: '2rem 2.5rem' },
		borderRadius: '1rem',
		background: 'var(--Background-Gradient-Dark, linear-gradient(165deg, #0C3867 4.91%, #125399 89.46%))',
	},
	ratingDisplay: {
		display: 'flex',
		flexDirection: 'column',
		gap: '0.5rem',
		minWidth: { md: '220px' },
	},
	barsSection: {
		flex: 1,
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: '0.4rem',
	},
	ratingRow: {
		display: 'flex',
		alignItems: 'center',
		gap: '0.75rem',
		width: '100%',
	},
	ratingBar: {
		flex: 1,
	},
	buttonsSection: {
		display: 'flex',
		flexDirection: 'column',
		gap: '0.75rem',
		minWidth: { md: '180px' },
	},
	reviewsContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: '1rem',
	},
	reviewCard: {
		p: '1rem 1.25rem',
		display: 'flex',
		flexDirection: 'column',
		gap: '0.75rem',
		borderRadius: '0.5rem',
		border: '1px solid',
		borderColor: 'neutrals.primary',
	},
	starRow: {
		display: 'flex',
	},
	reviewMeta: {
		display: 'flex',
		flexDirection: 'column',
		gap: '0.25rem',
	},
	viewAllContainer: {
		display: 'flex',
		justifyContent: 'center',
		pt: '0.5rem',
	},
};
